import { useEffect, useMemo, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PrincipleBanner from "@/components/PrincipleBanner";
import EventTicker from "@/components/EventTicker";
import { createRecorder } from "@/lib/telemetry";
import { api, finalizeCapability } from "@/lib/api";
import SimulationRunner from "@/components/sims/SimulationRunner";
import IDDLInbox from "@/components/sims/IDDLInbox";
import IDDLBrowser from "@/components/sims/IDDLBrowser";
import IDDLAIChat from "@/components/sims/IDDLAIChat";
import IDDLFileManager from "@/components/sims/IDDLFileManager";
import IDDLSettings from "@/components/sims/IDDLSettings";
import IDDLWorkflow from "@/components/sims/IDDLWorkflow";
import IDDLEditor from "@/components/sims/IDDLEditor";
import IDDLAIChatAgent from "@/components/sims/IDDLAIChatAgent";
import { useT } from "@/lib/i18n";

const NEW_ENGINE_SIM_TYPES = new Set([
  "iddl_inbox", "iddl_browser", "iddl_ai_chat", "iddl_file_manager",
  "iddl_settings", "iddl_workflow", "iddl_editor",
  "iddl_ai_chat_agent",
]);

// Primitives whose React renderer this component maps. Kept in sync
// with the render switch below. The unmapped-primitive fallback
// throws against this set so a future primitive added to
// NEW_ENGINE_SIM_TYPES but forgotten here surfaces immediately in
// the UI instead of silently rendering nothing.
const CAPABILITY_RENDERERS = new Set([
  "iddl_inbox", "iddl_browser", "iddl_ai_chat", "iddl_file_manager",
  "iddl_settings", "iddl_workflow", "iddl_editor",
  "iddl_ai_chat_agent",
]);

export default function CapabilityStep({ attempt, onAdvance }) {
  const { t, lang } = useT();
  const concept = attempt.current_concept;
  const [events, setEvents] = useState([]);
  const [engine, setEngine] = useState(null); // {use_new_engine, …} once fetched
  const [busy, setBusy] = useState(false);
  const [finalizedNew, setFinalizedNew] = useState(false);

  // Phase 2 — discover whether this concept runs on the new behavioural
  // engine. Fetch ONCE per concept per attempt. While loading, render a
  // neutral placeholder so we never accidentally render the legacy sim
  // for a new-engine concept (or vice versa).
  //
  // Iter-26 (P1) — also re-fetch whenever the active UI language
  // changes. The candidate's progress is INTACT (state lives server-
  // side); we just re-pull the localised presentation strings. This
  // is the IDDL multilingual contract: switching language is a
  // PRESENTATION-layer event and must not mutate state, events,
  // scores or timers.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api
          .get(`/attempts/${attempt.id}/current_engine`, { params: { lang } })
          .then((x) => x.data);
        if (alive) setEngine(r);
      } catch (_e) {
        if (alive) setEngine({ use_new_engine: false });
      }
    })();
    return () => { alive = false; };
  }, [attempt.id, concept.id, lang]);

  // Legacy telemetry recorder — only used when engine.use_new_engine === false
  const recorder = useMemo(
    () => createRecorder(attempt.id, concept.id, (rec) => setEvents((p) => [...p, rec])),
    // eslint-disable-next-line
    [attempt.id, concept.id],
  );

  const finalize = async (finalDecision, workspaceData = {}) => {
    setBusy(true);
    try {
      await finalizeCapability(attempt.id, {
        concept_id: concept.id,
        final_decision: finalDecision,
        workspace_data: workspaceData,
      });
      await onAdvance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // Used by the new IDDLInbox primitive — events are already logged to
  // /events by the inbox itself; we just need to flip the step forward.
  const finalizeNewEngine = async () => {
    if (finalizedNew) return;
    setFinalizedNew(true);
    await finalize("new_engine_finalize", { engine: "v2" });
  };

  const usingNew =
    engine?.use_new_engine === true && NEW_ENGINE_SIM_TYPES.has(engine?.simulation_type);

  return (
    <div className="pb-32" data-testid="capability-step">
      <PrincipleBanner />

      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-6">
        <div className="paper">
          <div className="p-5 border-b border-[var(--iddl-rule)] flex items-start gap-3">
            <div>
              <div className="label-mono">{t("step.capability_layer")}</div>
              <h2 className="font-display text-xl md:text-2xl font-bold mt-1" data-testid="capability-card-title">
                {t("step.capability_title", { concept: concept.title })}
              </h2>
              <p className="text-sm text-[var(--iddl-muted)] mt-1 max-w-3xl" data-testid="capability-card-subtitle">
                {t("step.capability_sub")}
              </p>
            </div>
            <div className="ms-auto label-mono">
              {usingNew ? engine.simulation_type.toUpperCase() : concept.capability.sim_type.toUpperCase()}
            </div>
          </div>

          <div className="p-5 surface border-b border-[var(--iddl-rule)]">
            <div className="label-mono">{t("step.capability_task")}</div>
            <p className="font-display text-base mt-1">
              {usingNew ? (engine.candidate_task || engine.candidate_goal || concept.capability.task) : concept.capability.task}
            </p>
          </div>

          {engine === null && (
            <div className="p-6 text-sm text-[var(--iddl-muted)]" data-testid="capability-loading">
              {t("common.loading")}
            </div>
          )}

          {engine && !usingNew && (
            <SimulationRunner
              simType={concept.capability.sim_type}
              scenario={concept.capability.scenario}
              recorder={recorder}
              onFinalize={finalize}
              busy={busy}
            />
          )}

          {usingNew && (
            <div className="p-3" data-testid="capability-new-engine">
              {engine.simulation_type === "iddl_inbox" && (
                <IDDLInbox
                  attemptId={attempt.id}
                  conceptId={concept.id}
                  scenarioText={engine.scenario_text}
                  onFinalize={finalizeNewEngine}
                />
              )}
              {engine.simulation_type === "iddl_browser" && (
                <IDDLBrowser
                  attemptId={attempt.id}
                  conceptId={concept.id}
                  scenarioText={engine.scenario_text}
                  onFinalize={finalizeNewEngine}
                />
              )}
              {engine.simulation_type === "iddl_ai_chat" && (
                <IDDLAIChat
                  attemptId={attempt.id}
                  conceptId={concept.id}
                  scenarioText={engine.scenario_text}
                  onFinalize={finalizeNewEngine}
                />
              )}
              {engine.simulation_type === "iddl_file_manager" && (
                <IDDLFileManager
                  attemptId={attempt.id}
                  conceptId={concept.id}
                  scenarioText={engine.scenario_text}
                  onFinalize={finalizeNewEngine}
                />
              )}
              {engine.simulation_type === "iddl_settings" && (
                <IDDLSettings
                  attemptId={attempt.id}
                  conceptId={concept.id}
                  scenarioText={engine.scenario_text}
                  onFinalize={finalizeNewEngine}
                />
              )}
              {engine.simulation_type === "iddl_workflow" && (
                <IDDLWorkflow
                  attemptId={attempt.id}
                  conceptId={concept.id}
                  scenarioText={engine.scenario_text}
                  onFinalize={finalizeNewEngine}
                />
              )}
              {engine.simulation_type === "iddl_editor" && (
                <IDDLEditor
                  attemptId={attempt.id}
                  conceptId={concept.id}
                  scenarioText={engine.scenario_text}
                  onFinalize={finalizeNewEngine}
                />
              )}
              {engine.simulation_type === "iddl_ai_chat_agent" && (
                <IDDLAIChatAgent
                  attemptId={attempt.id}
                  conceptId={concept.id}
                  scenarioText={engine.scenario_text}
                  onFinalize={finalizeNewEngine}
                />
              )}
              {/* Fallback: new-engine path reached but this component
                  has no renderer for the primitive. Throwing here
                  surfaces the mismatch loudly in the UI (and in the
                  nearest React error boundary) rather than letting a
                  blank panel ship. Add the missing branch above and
                  register the primitive in CAPABILITY_RENDERERS. */}
              {!CAPABILITY_RENDERERS.has(engine.simulation_type) && (() => {
                throw new Error(
                  `CapabilityStep: no renderer registered for simulation_type ` +
                  `"${engine.simulation_type}". Add a render branch and ` +
                  `register the primitive in CAPABILITY_RENDERERS.`
                );
              })()}
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-[var(--iddl-muted)] flex items-center justify-between">
          <span>{t("step.capability_events", { n: events.length })}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-none"
            disabled={busy || finalizedNew}
            data-testid="btn-skip-end-capability"
            onClick={() => finalize("__abandoned__", { abandoned: true })}
          >
            {t("step.capability_end_early")} <ArrowRight size={12} className="ml-2" />
          </Button>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0">
        <EventTicker events={events} />
      </div>
    </div>
  );
}
